import { ADMIN } from "../constans/roles";
import { ZAKODOWANE, WIZUALIZACJE } from "../constans/tableCalls";
import {
  getRandomTableByFormat,
  getTableCount,
  getTableCountPerFormat,
} from "../utils/api/tableApi";
import { getUserProfile } from "../utils/api/userApi";

export const DASHBOARD_FORMAT_POOL = [
  "PLAY.me",
  "ENGAGE.touch",
  "EASY.roll",
  "SOFT.banner",
  "GAME.changer",
  "FLIP.twist",
  "WAVE.card",
  "SCROLL.me",
  "POKER.slide",
  "COUNT.down",
  "GUESS.what",
  "CALENDAR.invite",
  "SPORT.live"
];

export const DASHBOARD_CHART_COLORS = [
  "#e85511",
  "#f08d19",
  "#f4c14f",
  "#b53b2d",
  "#7a4b2a",
  "#55504c",
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toCount = (payload) => toNumber(payload?.count);

const extractFormatEntries = (payload) => {
  if (Array.isArray(payload?.formats)) {
    return payload.formats;
  }

  if (payload?.formats && typeof payload.formats === "object") {
    return Object.entries(payload.formats).map(([format, count]) => ({
      format,
      count,
    }));
  }

  return [];
};

const normalizeFormatEntry = (item) => {
  if (!item || typeof item !== "object") {
    return null;
  }

  const format =
    item.format ||
    item.Format ||
    item._id ||
    item.name ||
    item.label ||
    item.key ||
    "";

  const count =
    item.count ??
    item.total ??
    item.value ??
    item.ilosc ??
    item.counts ??
    0;

  const cleanedFormat = String(format).trim();
  if (!cleanedFormat) {
    return null;
  }

  return {
    format: cleanedFormat,
    count: toNumber(count),
  };
};

const normalizeFormats = (payload) =>
  extractFormatEntries(payload)
    .map(normalizeFormatEntry)
    .filter((entry) => entry && entry.count > 0);

const pickRandomFormats = (pool, size = 3) => {
  const copy = [...pool];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const randomIndex = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[randomIndex]] = [copy[randomIndex], copy[i]];
  }
  return copy.slice(0, Math.max(0, Math.min(size, copy.length)));
};

const pickObjectWithLink = (candidate) => {
  if (!candidate) return null;

  if (Array.isArray(candidate)) {
    for (let i = 0; i < candidate.length; i += 1) {
      const nested = pickObjectWithLink(candidate[i]);
      if (nested) return nested;
    }
    return null;
  }

  if (typeof candidate !== "object") {
    return null;
  }

  if (candidate.Link || candidate.link || candidate.url || candidate.href) {
    return candidate;
  }

  const keys = Object.keys(candidate);
  for (let i = 0; i < keys.length; i += 1) {
    const nested = pickObjectWithLink(candidate[keys[i]]);
    if (nested) return nested;
  }

  return null;
};

const ensurePreviewIframeLink = (rawLink) => {
  const link = String(rawLink || "").trim();
  if (!link) return "";
  if (link.endsWith("/test/index.html")) return link;
  return `${link.replace(/\/+$/, "")}/test/index.html`;
};

export const buildDashboardActions = ({ role, choosenTable, onSelectTable, onLogout }) => {
  const actions = [
    {
      id: "panel-static",
      label: "Panel wizualizacji",
      description: "Tabela kreacji statycznych",
      icon: "📸",
      onClick: () => onSelectTable(WIZUALIZACJE),
      isActive: choosenTable === WIZUALIZACJE,
      visible: true,
      tone: "orange",
    },
    {
      id: "panel-coded",
      label: "Panel animacji",
      description: "Tabela kreacji zakodowanych",
      icon: "💻",
      onClick: () => onSelectTable(ZAKODOWANE),
      isActive: choosenTable === ZAKODOWANE,
      visible: true,
      tone: "blue",
    },
    {
      id: "slideshow",
      label: "Slideshow",
      description: "Dodawanie wizualizacji",
      icon: "🎞️",
      onClick: () => onSelectTable(null, "/slideshow"),
      visible: true,
      tone: "green",
    },
    {
      id: "footer",
      label: "Generator stopki",
      description: "Tworzenie stopki e-mail",
      icon: "✉️",
      onClick: () => onSelectTable(null, "/footer"),
      visible: true,
      tone: "sand",
    },
    {
      id: "links",
      label: "Kreator linkow",
      description: "Dodawanie reklam ZIP",
      icon: "💽",
      onClick: () => onSelectTable(null, "/linki"),
      visible: role === ADMIN,
      tone: "violet",
    },
    {
      id: "register",
      label: "Dodaj uzytkownika",
      description: "Formularz rejestracji",
      icon: "📋",
      onClick: () => onSelectTable(null, "/register"),
      visible: role === ADMIN,
      tone: "dark",
    },
    {
      id: "logout",
      label: "Wyloguj",
      description: "Papa :C",
      icon: "💔",
      onClick: onLogout,
      visible: true,
      tone: "red",
    },
  ];

  return actions.filter((action) => action.visible);
};

export async function loadHomeProfile() {
  const response = await getUserProfile();
  const payload = response?.data || {};

  return {
    name: payload?.name || "User",
    img: payload?.img || "./Images/userIco.png",
  };
}

export async function loadDashboardCountsAndFormats() {
  const [visualCountRes, codedCountRes, visualFormatsRes, codedFormatsRes] = await Promise.all([
    getTableCount(WIZUALIZACJE),
    getTableCount(ZAKODOWANE),
    getTableCountPerFormat(WIZUALIZACJE),
    getTableCountPerFormat(ZAKODOWANE),
  ]);

  return {
    visualCount: toCount(visualCountRes?.data),
    codedCount: toCount(codedCountRes?.data),
    visualFormats: normalizeFormats(visualFormatsRes?.data),
    codedFormats: normalizeFormats(codedFormatsRes?.data),
  };
}

export async function loadRandomAnimatedPreviewFrames(formatPool, desiredCount = 3) {
  const selectedFormats = pickRandomFormats(formatPool, desiredCount);

  const frames = await Promise.all(selectedFormats.map((format) => loadRandomAnimatedFrameByFormat(format)));

  return frames;
}

export async function loadRandomAnimatedFrameByFormat(format) {
  try {
    const response = await getRandomTableByFormat(ZAKODOWANE, format);
    const item = pickObjectWithLink(response?.data);
    const rawLink = item?.Link || item?.link || item?.url || item?.href || "";

    return {
      format,
      link: ensurePreviewIframeLink(rawLink),
      sourceTitle: item?.Kampania || item?.campaign || item?.name || "Podglad",
    };
  } catch (error) {
    return {
      format,
      link: "",
      sourceTitle: "Brak danych",
    };
  }
}

export async function loadRandomAnimatedPreviewFramesForFormat(format, desiredCount = 3) {
  const cleanedFormat = String(format || "").trim();
  if (!cleanedFormat) {
    return [];
  }

  const frames = await Promise.all(
    Array.from({ length: desiredCount }).map(() => loadRandomAnimatedFrameByFormat(cleanedFormat))
  );

  return frames;
}
